import React, { FC } from 'react';
import './PlanningWidget.css';
import { Modal, Widget } from '@/Shared/UI/Components';
import { usePlanningWidget } from '../../Logic/Hooks';
import { CalendarWidget, DOMAIN_TYPES, GenericDomainDetail, useModal } from '@/Shared';
import { CalendarRow } from '@/Shared/UI/Widgets/CalendarWidget/CalendarWidget';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';
import GenericDomainForm from '@/Shared/UI/Forms/GenericDomainForm';

interface PlanningWidgetProps { }

/**
 * PlanningWidget component
 *
 * High-level widget responsible for displaying the planning calendar.
 * It acts as a composition layer between:
 * - the planning widget data source,
 * - the calendar UI,
 * - and modal-based interactions (create / view planning entries).
 *
 * Responsibilities:
 * - Render a calendar view based on planning data
 * - Open a modal to create a new planning entry when a time slot is selected
 * - Open a modal to view planning details when an existing event is selected
 *
 * This component contains no domain logic.
 * All business rules are delegated to hooks and domain-specific components.
 */
const PlanningWidget: FC<PlanningWidgetProps> = () => {

	/**
	 * Modal controller used to display forms and detail views
	 */
	const modalHook = useModal();

	/**
	 * Fetches calendar data and loading/error state
	 */
	const { data, response } = usePlanningWidget();


	/**
	 * Handles selection of a time slot in the calendar.
	 * Opens the PlanningForm inside a modal, pre-filled
	 * with the selected start and end dates.
	 */
	const handleSelectSlot = (start: Date, end: Date) => {
		modalHook.openModal({
			title: "Calendrier",
			content: <GenericDomainForm  
				domainType={DOMAIN_TYPES.Planning} 
				inModalHook={modalHook} 
				extraArgs={[start, end]}
			/>
		});
	}


	/**
	 * Handles selection of an existing calendar event.
	 * Opens the Planning detail view inside a modal.
	 */
	const handleSelectEvent = (item: CalendarRow) => {
		modalHook.openModal({
			title: "Calendrier",
			content: <GenericDomainDetail
				domainType={DOMAIN_TYPES.Planning}
				inModalHook={modalHook}
				extraArgs={{ entityId : item.id }}
			/>
		});
	}


	/**
	 * Builds the CalendarWidget component once data is available.
	 * Returns undefined while data is loading.
	 */
	const getCalendarWidget = () => {
		return data ? <CalendarWidget
			eventsData={data?.rows}
			resources={data?.resources}
			colorByKey="type"
			filterByKey="person"
			eventTitleGetter={(event) => event.note ?? `Tâche ${event.type ?? ''}`}
			onSelectEvent={handleSelectEvent}
			onSelectSlot={handleSelectSlot}
		/> : undefined;
	}

	return (
		<>
			{/* Global modal container for planning forms and details */}
			<Modal
				className='w-80 minH-80'
				onClose={modalHook.closeModal}
				data={modalHook.modalData}
			/>

			{/* Widget wrapper handling loading and error states */}
			<Widget
				domainLabel={DOMAIN_CONFIGS.Planning.domainLabel}
				title='Calendrier'
				classes='PlanningWidget'
				response={response}
				content={getCalendarWidget()} />
		</>

	);
}

export default PlanningWidget;
